import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  ShieldCheck, Send, MessageCircle, CheckCircle2, Clock, Circle, AlertTriangle,
  Search, Filter, ChevronDown, ChevronUp, FileSignature,
} from 'lucide-react'
import { FeaturedLayout } from '../layouts'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import {
  Button, Input, Card, CardContent, Badge, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../ui'
import { getConsentimientosResumen, solicitarConsentimientoParaJugadores, jugadoresEnTorneosDeRed } from '../../lib/supabase'
import SolicitarConsentimientoDialog from '../consent/SolicitarConsentimientoDialog'
import TemporadaSelector from './pagos/TemporadaSelector'
import { useTemporadaActiva } from './pagos/useTemporadaActiva'
import { useInscritosTemporada } from '../../hooks/useInscritosTemporada'
import { FiltroInscritos, AvisoSinPadron } from './FiltroInscritos'

const ESTADO_META = {
  firmado: { label: 'Firmado', icon: CheckCircle2, cls: 'bg-green-100 text-green-800' },
  // Firmas del flujo viejo: el tutor abrio un link y ya. No prueban quien lo abrio,
  // asi que no cuentan como consentimiento y hay que volver a pedirlas.
  sin_verificar: { label: 'Sin verificar', icon: AlertTriangle, cls: 'bg-orange-100 text-orange-800' },
  enviado: { label: 'Pendiente de firma', icon: Clock, cls: 'bg-amber-100 text-amber-800' },
  sin_solicitar: { label: 'Sin solicitar', icon: Circle, cls: 'bg-slate-100 text-slate-700' },
}

const fmtFecha = (d) => {
  if (!d) return '—'
  try { return format(parseISO(d), 'dd/MM/yyyy', { locale: es }) } catch { return '—' }
}

const ConsentimientosDashboard = () => {
  const { org } = useOrganization()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [estadoFilter, setEstadoFilter] = useState('todos')
  const [grupoFilter, setGrupoFilter] = useState('todos')

  // Mismo control que las otras seis pantallas: por defecto solo los inscritos,
  // con escape para ver al resto. Aqui habia un selector propio que arrancaba en
  // "todo el padron" -- funcionaba, pero se comportaba distinto y no tenia las
  // valvulas del hook (no filtrar si la temporada no tiene padron, ni si falla).
  const { temporadaId, temporadas, setTemporadaId } = useTemporadaActiva()
  const inscritosEstado = useInscritosTemporada(temporadaId)
  const [dialog, setDialog] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  // Pieza 2b: quien esta en el roster de un torneo de la red que sigue vivo (para el filtro
  // "Falta firma para torneos"). Si la lectura falla se dice y no se ofrece el filtro.
  const [enTorneo, setEnTorneo] = useState(() => new Set())
  const [errorTorneos, setErrorTorneos] = useState(false)
  const [soloTorneos, setSoloTorneos] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [{ data }, torneos] = await Promise.all([
      getConsentimientosResumen(org?.id),
      jugadoresEnTorneosDeRed(org?.id),
    ])
    setRows(data || [])
    setEnTorneo(new Set(torneos?.data || []))
    setErrorTorneos(!!torneos?.error)
    // Sin la lista de torneos el filtro no sabe a quien dejar: se quita, no se queda en "0".
    if (torneos?.error) setSoloTorneos(false)
    setSelected(new Set())
    setLoading(false)
  }, [org?.id])

  useEffect(() => { if (org?.id) load() }, [org?.id, load])

  const kpis = useMemo(() => {
    const total = rows.length
    const firmados = rows.filter((r) => r.estado === 'firmado').length
    const enviados = rows.filter((r) => r.estado === 'enviado').length
    const sinSolicitar = rows.filter((r) => r.estado === 'sin_solicitar').length
    const sinVerificar = rows.filter((r) => r.estado === 'sin_verificar').length
    const pct = total ? Math.round((firmados / total) * 100) : 0
    return { total, firmados, enviados, sinSolicitar, sinVerificar, pct }
  }, [rows])

  // Va a un torneo de la red y su firma no cubre torneos: juega, pero el roster lo avisa hasta
  // que su tutor firme el Aviso nuevo (validar_roster_inscripcion, regla firma_tutor, warn).
  const faltaFirmaTorneo = useCallback(
    (r) => enTorneo.has(r.jugadorId) && !r.cubreTorneos,
    [enTorneo]
  )
  const faltanTorneo = useMemo(() => rows.filter(faltaFirmaTorneo).length, [rows, faltaFirmaTorneo])

  const visibles = useMemo(() => {
    const t = searchTerm.trim().toLowerCase()
    return rows.filter((r) => {
      const matchEstado = estadoFilter === 'todos' || r.estado === estadoFilter
      const matchGrupo = grupoFilter === 'todos'
        || (r.grupos || []).some((g) => g.id === grupoFilter)
      const matchTorneo = !soloTorneos || faltaFirmaTorneo(r)
      const matchSearch =
        !t ||
        r.nombre?.toLowerCase().includes(t) ||
        r.tutorNombre?.toLowerCase().includes(t) ||
        r.familiaNombre?.toLowerCase().includes(t)
      return matchEstado && matchGrupo && matchTorneo && matchSearch
    })
  }, [rows, inscritosEstado, estadoFilter, grupoFilter, soloTorneos, faltaFirmaTorneo, searchTerm])

  const hasActiveFilters = estadoFilter !== 'todos' || grupoFilter !== 'todos' || soloTorneos

  // Opciones a partir de lo que realmente hay en el padron, sin consultas extra.
  const gruposDisponibles = useMemo(() => {
    const m = new Map()
    rows.forEach((r) => (r.grupos || []).forEach((g) => m.set(g.id, g)))
    return [...m.values()].sort((a, b) => (a.nombre_del_grupo || '').localeCompare(b.nombre_del_grupo || ''))
  }, [rows])

  // Se puede pedir firma a quien no ha firmado y, desde la pieza 2b, a quien firmo pero va a un
  // torneo con una firma que no lo cubre.
  const sePuedePedir = useCallback((r) => r.estado !== 'firmado' || faltaFirmaTorneo(r), [faltaFirmaTorneo])
  const seleccionables = useMemo(() => visibles.filter(sePuedePedir), [visibles, sePuedePedir])
  const allSelected = seleccionables.length > 0 && seleccionables.every((r) => selected.has(r.jugadorId))

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelected((prev) =>
      seleccionables.every((r) => prev.has(r.jugadorId))
        ? new Set()
        : new Set(seleccionables.map((r) => r.jugadorId))
    )
  }

  const handleSolicitarSeleccionados = async () => {
    const ids = [...selected]
    if (ids.length === 0) return
    const ok = await confirm(
      'Solicitar consentimiento',
      `Se generará una solicitud para los ${ids.length} jugador(es) seleccionado(s) (un solo enlace por familia). Después podrás compartir cada enlace por WhatsApp.`
    )
    if (!ok) return
    setBulkLoading(true)
    const { data, error } = await solicitarConsentimientoParaJugadores(ids, org?.id)
    setBulkLoading(false)
    if (error) { await alert('Error', 'No se pudieron generar las solicitudes.'); return }
    await load()
    await alert('Listo', `Se generaron ${data.solicitudes} solicitud(es) cubriendo ${data.jugadores} jugador(es). Compártelas por WhatsApp desde la columna Acción.`)
  }

  const renderAccion = (r) => {
    if (r.estado === 'firmado' && faltaFirmaTorneo(r)) {
      return (
        <Button variant="outline" size="sm" onClick={() => setDialog({ ...r, existingToken: null })}>
          <MessageCircle className="h-3.5 w-3.5 mr-1" /> Volver a pedir
        </Button>
      )
    }
    if (r.estado === 'firmado') {
      return (
        <div className="text-xs text-muted-foreground">
          {fmtFecha(r.fechaFirma)}
          {r.usaImagen && <Badge variant="outline" className="ml-2 text-[10px]">Imagen ✓</Badge>}
        </div>
      )
    }
    if (r.estado === 'enviado') {
      return (
        <Button variant="outline" size="sm" onClick={() => setDialog({ ...r, existingToken: r.token })}>
          <MessageCircle className="h-3.5 w-3.5 mr-1" /> Recordar
        </Button>
      )
    }
    if (r.estado === 'sin_verificar') {
      return (
        <Button variant="outline" size="sm" onClick={() => setDialog({ ...r, existingToken: null })}>
          <MessageCircle className="h-3.5 w-3.5 mr-1" /> Volver a pedir
        </Button>
      )
    }
    return (
      <Button variant="outline" size="sm" onClick={() => setDialog({ ...r, existingToken: null })}>
        <Send className="h-3.5 w-3.5 mr-1" /> Solicitar
      </Button>
    )
  }

  return (
    <FeaturedLayout
      title="Consentimientos"
      subtitle="Seguimiento del consentimiento de los tutores"
      refreshAction={load}
    >
      {temporadas.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <TemporadaSelector temporadas={temporadas} value={temporadaId} onChange={setTemporadaId} />
          <FiltroInscritos estado={inscritosEstado} id="cons-ver-no-inscritos" />
        </div>
      )}
      {inscritosEstado.sinPadron && (
        <div className="mb-4"><AvisoSinPadron estado={inscritosEstado} /></div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">Firmados</p>
          <p className="text-2xl font-bold text-green-700">{kpis.firmados}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">Sin verificar</p>
          <p className="text-2xl font-bold text-orange-600">{kpis.sinVerificar}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">Pendientes</p>
          <p className="text-2xl font-bold text-amber-600">{kpis.enviados}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">Sin solicitar</p>
          <p className="text-2xl font-bold text-slate-600">{kpis.sinSolicitar}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">% Firmados</p>
          <p className="text-2xl font-bold">{kpis.pct}%</p>
        </CardContent></Card>
      </div>

      {kpis.sinVerificar > 0 && (
        <div className="mb-6 rounded-md bg-orange-50 px-4 py-3 text-sm text-orange-900">
          <strong>{kpis.sinVerificar} firma(s) se recabaron con el flujo anterior</strong>, en el que
          bastaba abrir un enlace. Un enlace se reenvía y no prueba quién lo abrió, así que no cuentan
          como consentimiento válido. Selecciónalas y vuelve a pedirlas: ahora el tutor confirma con un
          código que le llega por correo.
        </div>
      )}
      {(faltanTorneo > 0 || soloTorneos) && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <p>
            <strong>{faltanTorneo} jugador(es) van a un torneo y su firma no cubre torneos.</strong>{' '}
            Pueden jugar; pide a su tutor que firme el Aviso de Privacidad nuevo.
          </p>
          <Button
            variant={soloTorneos ? 'default' : 'outline'}
            size="sm"
            aria-pressed={soloTorneos}
            onClick={() => setSoloTorneos((v) => !v)}
          >
            Falta firma para torneos
          </Button>
        </div>
      )}
      {errorTorneos && (
        <div className="mb-6 rounded-md bg-muted px-4 py-3 text-sm text-muted-foreground">
          No se pudo revisar qué jugadores van a un torneo. Recarga la página.
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-6">
        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Buscar por jugador o tutor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block">
              <span className="font-medium text-foreground">{visibles.length}</span> jugadores
            </span>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3"
            >
              <Filter className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Filtros</span>
              {showFilters ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" /> : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
            </Button>
            <Button className="h-10" onClick={handleSolicitarSeleccionados} disabled={bulkLoading || selected.size === 0}>
              <ShieldCheck className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">
                {bulkLoading ? 'Generando…' : `Solicitar${selected.size ? ` (${selected.size})` : ''}`}
              </span>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {gruposDisponibles.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Grupo</Label>
                <Select value={grupoFilter} onValueChange={setGrupoFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {gruposDisponibles.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.nombre_del_grupo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Estado</Label>
              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="sin_solicitar">Sin solicitar</SelectItem>
                  <SelectItem value="sin_verificar">Sin verificar</SelectItem>
                  <SelectItem value="enviado">Pendientes de firma</SelectItem>
                  <SelectItem value="firmado">Firmados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => { setEstadoFilter('todos'); setGrupoFilter('todos'); setSoloTorneos(false) }}
              >
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Cargando…</div>
      ) : visibles.length === 0 ? (
        <div className="text-center py-16">
          <FileSignature className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-foreground font-medium mb-1">
            {searchTerm || hasActiveFilters ? 'No se encontraron jugadores' : 'No hay jugadores registrados'}
          </p>
          <p className="text-muted-foreground text-sm">
            {searchTerm || hasActiveFilters ? 'Intenta con otros filtros' : 'Agrega jugadores para solicitar su consentimiento'}
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input align-middle"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={seleccionables.length === 0}
                      title="Seleccionar todos"
                    />
                  </TableHead>
                  <TableHead>Jugador</TableHead>
                  <TableHead>Tutor / Familia</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibles.map((r) => {
                  const meta = ESTADO_META[r.estado] || ESTADO_META.sin_solicitar
                  const Icon = meta.icon
                  return (
                    <TableRow key={r.jugadorId} data-state={selected.has(r.jugadorId) ? 'selected' : undefined}>
                      <TableCell className="w-10">
                        {sePuedePedir(r) && (
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-input align-middle"
                            checked={selected.has(r.jugadorId)}
                            onChange={() => toggleOne(r.jugadorId)}
                            aria-label={`Seleccionar a ${r.nombre}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{r.nombre}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.tutorNombre || r.familiaNombre || '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge className={`${meta.cls} gap-1`}>
                            <Icon className="h-3 w-3" /> {meta.label}
                          </Badge>
                          {faltaFirmaTorneo(r) && (
                            <Badge variant="outline" className="border-sky-300 bg-sky-50 text-sky-800 text-[10px]">
                              Falta firma para torneos
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{renderAccion(r)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {dialog && (
        <SolicitarConsentimientoDialog
          open={!!dialog}
          onOpenChange={(v) => { if (!v) { setDialog(null); load() } }}
          jugadorId={dialog.jugadorId}
          familiaId={dialog.familiaId}
          tutorTelefono={dialog.tutorTelefono}
          tituloDestino={dialog.nombre}
          existingToken={dialog.existingToken}
        />
      )}
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default ConsentimientosDashboard
